<template>
  <div class="border-solid border-0 border-t border-gray-border pt-6">
    <base-label id="agentSettings"
                label="AI agent"
                tooltip="Who the assistant is and how it writes.<br/>Added to every GPT action of this bot, ahead of the action's own prompt."
    />
    <p v-if="isMultiBot" class="mt-1 text-sm text-gray-dark">
      Applies to all market variants of this bot. Each market keeps its own reply language.
    </p>
    <p v-if="isEmailBot" class="mt-1 text-sm text-gray-dark">
      Setting Formality, Emojis or Reply length replaces the default email tone (professional, no emojis).
    </p>

    <div class="mt-3 grid grid-cols-2 gap-4">
      <div>
        <base-label id="agentName" label="Assistant name" :label-classes="'font-medium'"/>
        <base-input id="agentName"
                    variant="editor"
                    :value="form[KEYS.NAME]"
                    placeholder="e.g. Anna"
                    class="w-full border-gray py-px mt-1 h-10"
                    @input="update(KEYS.NAME, $event)"
        />
      </div>
      <div>
        <base-label id="agentGender" label="Gender" :label-classes="'font-medium'"/>
        <queryable-select id="agentGender"
                          :value="form[KEYS.GENDER]"
                          :options="GENDER_OPTIONS"
                          class="w-full mt-1 h-10"
                          @input="update(KEYS.GENDER, $event)"
        />
      </div>
      <div>
        <base-label id="agentRole" label="Role" :label-classes="'font-medium'"/>
        <base-input id="agentRole"
                    variant="editor"
                    :value="form[KEYS.ROLE]"
                    placeholder="AI shopping assistant"
                    class="w-full border-gray py-px mt-1 h-10"
                    @input="update(KEYS.ROLE, $event)"
        />
      </div>
      <div>
        <base-label id="agentCompany" label="Company" :label-classes="'font-medium'"/>
        <base-input id="agentCompany"
                    variant="editor"
                    :value="form[KEYS.COMPANY]"
                    :placeholder="companySuggestion"
                    class="w-full border-gray py-px mt-1 h-10"
                    @input="update(KEYS.COMPANY, $event)"
        />
      </div>
      <div>
        <base-label id="agentFormality" label="Formality" :label-classes="'font-medium'"/>
        <queryable-select id="agentFormality"
                          :value="form[KEYS.FORMALITY]"
                          :options="FORMALITY_OPTIONS"
                          class="w-full mt-1 h-10"
                          @input="update(KEYS.FORMALITY, $event)"
        />
      </div>
      <div>
        <base-label id="agentEmoji" label="Emojis" :label-classes="'font-medium'"/>
        <queryable-select id="agentEmoji"
                          :value="form[KEYS.EMOJI]"
                          :options="EMOJI_OPTIONS"
                          class="w-full mt-1 h-10"
                          @input="update(KEYS.EMOJI, $event)"
        />
      </div>
      <div>
        <base-label id="agentReplyLength" label="Reply length" :label-classes="'font-medium'"/>
        <queryable-select id="agentReplyLength"
                          :value="form[KEYS.REPLY_LENGTH]"
                          :options="REPLY_LENGTH_OPTIONS"
                          class="w-full mt-1 h-10"
                          @input="update(KEYS.REPLY_LENGTH, $event)"
        />
      </div>
    </div>

    <!-- Read-only: rendered by the engine with the same builder the bot runs on, so it cannot drift from what
         the model is actually sent. Empty until something is configured — that is also the "off" state.
         Not BaseTextPreview: that hardcodes `break-all`, which chops words mid-letter ("manufactur er").
         Text interpolation is escaped by Vue, so this drops a v-html sink too. -->
    <div class="mt-6">
      <base-label id="agentPreview"
                  label="Prompt added to every GPT action"
                  :is-admin="true"
                  tooltip="Exactly what the engine prepends, for this bot's language.<br/>Read-only — it is built from the fields above."
      />
      <div v-if="preview"
           class="mt-1 p-3 rounded bg-soft text-sm font-mono leading-normal whitespace-pre-wrap break-words overflow-y-auto"
           style="max-height: 15rem"
      >{{ preview }}</div>
      <p v-else class="mt-1 text-sm text-gray">
        Nothing is added to the prompts yet. Fill in any field above to switch the agent block on.
      </p>
    </div>

    <div class="mt-4 flex items-center gap-3">
      <busy-button id="save-agent-settings"
                   variant="primary"
                   class="h-10 px-6"
                   :is-loading="isSaving"
                   :disabled="!isDirty"
                   @click.prevent.stop="save"
      >
        Save
      </busy-button>
      <span v-if="isDirty" class="text-sm text-gray">Unsaved changes</span>
    </div>
  </div>
</template>

<script>
import BaseInput from '@/components/base-components/BaseInput.vue'
import BaseLabel from '@/components/base-components/BaseLabel.vue'
import BusyButton from '@/components/common/BusyButton.vue'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import agentSettingsService from '@/services/amio/agent-settings.service'
import {
  AGENT_SETTING_KEYS,
  EMOJI_OPTIONS,
  FORMALITY_OPTIONS,
  GENDER_OPTIONS,
  REPLY_LENGTH_OPTIONS
} from '@/utils/agent-settings.constants'

const EMPTY_FORM = () => Object.values(AGENT_SETTING_KEYS).reduce((form, key) => ({ ...form, [key]: '' }), {})

export default {
  name: 'SettingsBotAgent',

  components: { BaseInput, BaseLabel, BusyButton, QueryableSelect },

  props: {
    bot: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      KEYS: AGENT_SETTING_KEYS,
      GENDER_OPTIONS,
      FORMALITY_OPTIONS,
      EMOJI_OPTIONS,
      REPLY_LENGTH_OPTIONS,
      form: EMPTY_FORM(),
      saved: EMPTY_FORM(),
      preview: '',
      companySuggestion: '',
      isSaving: false
    }
  },

  computed: {
    isMultiBot() {
      return !!this.bot.multi_bot_id
    },
    // Email turns carry a hardcoded tone of their own, which a configured tone replaces — worth saying here,
    // because otherwise "Emojis: in moderation" looks like it does nothing on a channel that banned them.
    isEmailBot() {
      return this.bot.type === 'email'
    },
    isDirty() {
      return Object.values(AGENT_SETTING_KEYS).some(key => this.form[key] !== this.saved[key])
    }
  },

  watch: {
    // The panel is reused when another bot is selected, so reload rather than show the previous bot's settings.
    'bot.id': {
      immediate: true,
      handler() {
        this.load()
      }
    }
  },

  methods: {
    async load() {
      try {
        this.apply(await agentSettingsService.get(this.bot.id))
      } catch (err) {
        this.$vs.notify({ title: 'Could not load AI agent settings', text: err.message, color: 'danger' })
      }
    },

    async save() {
      this.isSaving = true
      try {
        this.apply(await agentSettingsService.update(this.bot.id, this.form))
        this.$vs.notify({ title: 'AI agent settings saved', color: 'success' })
      } catch (err) {
        this.$vs.notify({ title: 'Could not save AI agent settings', text: err.message, color: 'danger' })
      } finally {
        this.isSaving = false
      }
    },

    // The server is the source of truth for both halves: it drops blanked values, and it re-renders the
    // preview from what it actually stored.
    apply({ settings, preview, companySuggestion }) {
      this.form = { ...EMPTY_FORM(), ...settings }
      this.saved = { ...this.form }
      this.preview = preview || ''
      this.companySuggestion = companySuggestion || ''
    },

    update(key, value) {
      this.form = { ...this.form, [key]: value }
    }
  }
}
</script>
